/**
 * The SIV hexagon mark, rebuilt as vector.
 *
 * Four brand fields read left-to-right as the product's own story:
 * blue (data) → amber (insight) → emerald (growth) → navy (foundation),
 * unified by the white validation check. Drawn rather than embedded so it
 * stays crisp at every size, needs no network request, and can be recoloured.
 */

const HEX = 'M60 0 L120 33 L120 99 L60 132 L0 99 L0 33 Z';

export const BRAND = {
  blue: '#1F6FEB',
  amber: '#F5A623',
  emerald: '#12A870',
  navy: '#0F1E33',
  white: '#FFFFFF',
} as const;

type Props = {
  /** Rendered size in px (the mark is square-ish, 120×132 in user units). */
  size?: number;
  className?: string;
  /** Set when the mark is the only content of a link or button. */
  title?: string;
};

export function SivMark({ size = 36, className = '', title }: Props) {
  const uid = 'siv-mark';

  return (
    <svg
      viewBox="0 0 120 132"
      width={size}
      height={(size * 132) / 120}
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <clipPath id={`${uid}-hex`}>
          <path d={HEX} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${uid}-hex)`}>
        {/* Navy is the base; the other fields are laid over it. */}
        <rect width="120" height="132" fill={BRAND.navy} />
        <rect x="0" y="0" width="29" height="78" fill={BRAND.blue} />
        <rect x="29" y="0" width="45" height="61" fill={BRAND.amber} />
        <rect x="74" y="0" width="46" height="94" fill={BRAND.emerald} />

        {/* Data bars, kept inside the blue field and clear of the check. */}
        <g fill={BRAND.white}>
          <rect x="6" y="60" width="5" height="14" />
          <rect x="14" y="50" width="5" height="24" />
          <rect x="22" y="43" width="5" height="31" />
        </g>

        {/* Insight sparkle. */}
        <path
          d="M60 6 C61.6 14.4 65.6 18.4 74 20 C65.6 21.6 61.6 25.6 60 34 C58.4 25.6 54.4 21.6 46 20 C54.4 18.4 58.4 14.4 60 6 Z"
          fill={BRAND.white}
        />

        {/* Growth sprout. */}
        <path d="M60 72 C46 66 38.5 52 40.5 37 C55 41.5 62.5 56 60 72 Z" fill={BRAND.emerald} />
        <path d="M60 72 C74 66 81.5 52 79.5 37 C65 41.5 57.5 56 60 72 Z" fill={BRAND.emerald} />
        <path d="M58.5 60 h3 v40 h-3 Z" fill={BRAND.emerald} />

        {/* The validation check, over everything. */}
        <path
          d="M10 82 L57 121 L120 38"
          fill="none"
          stroke={BRAND.white}
          strokeWidth="15"
          strokeLinejoin="miter"
          strokeLinecap="butt"
        />
      </g>
    </svg>
  );
}
